/**
 * weather-edit-form.js
 */

import React from 'react';
import axios from 'axios';
import { useMediaQuery, Grid, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Autocomplete } from '@mui/material';
import useMounted from 'utils/use-mounted';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import Button from 'components/button';
import Icon from 'components/icon';


const useStyles = makeStyles(theme => ({
  reloadButton: {
    marginBottom: theme.spacing(2),
  },
}));


export default function WeatherEditForm(props) {
  const {
    value,
    onChange,
    onFocus,
    onBlur,
    onInit: onInitProp,
    doAutoFill,
    setLoading,
    location: {
      address1,
      city,
      region,
      country,
      postCode,
    },
    datetime,
  } = props;
  const isMounted = useMounted();
  const upSm = useMediaQuery(theme => theme.breakpoints.up("sm"), { noSsr: true });
  const [options, setOptions] = React.useState({
    conditions: [],
    temperature: [],
    windSpeed: [],
    windDirection: []
  });
  const [onInit] = React.useState(() => (val, shouldDirty) => onInitProp(val, shouldDirty));
  const classes = useStyles();


  // get weather options on init
  React.useEffect(() => {
    (async () => {
      const response = await axios.get(`/weather/options`);
      if (!isMounted.current) return;
      if (response.status !== 200) return;      
      const weatherOptions = response.data;
      setOptions(weatherOptions);
    })();
  }, [isMounted]);


  // weather autofill implementation
  const reloadWeather = React.useCallback((shouldDirty) => {
    if (city && region && country && datetime) {
      setLoading(true);
      (async () => {
        try {
          const response = await axios.get('/weather/address', {
            params: {address1, city, region, country, postCode, datetime},
            bypassErrorHandling: true
          });
          if (!isMounted.current) return;
          if (response.status !== 200) return;
          const weather = response.data;
          onInit(weather, shouldDirty);
        } catch (e) {
          if (!isMounted.current) return;
          onInit({
            conditions: '',
            temperature: '',
            windSpeed: '',
            windDirection: ''
          }, shouldDirty);
        } finally {
          if (!isMounted.current) return;
          setLoading(false);
        }
      })();
    }
  }, [address1, city, region, country, postCode, datetime, onInit, isMounted, setLoading]);


  // weather autofill effect
  React.useEffect(() => {
    if (doAutoFill) {
      reloadWeather(false);
    }
  }, [doAutoFill, address1, city, region, country, datetime, onInit, isMounted, setLoading, reloadWeather]);


  // on manual change
  const onWeatherChange = (type, val) => {
    onChange({
      ...(value ?? {}),
      [type]: val
    });
  }


  return (
    <>
        <Button 
          startIcon={<Icon type='refresh' fontSize="small" />}
          size="small"
          className={classes.reloadButton}
          onClick={() => reloadWeather(true)}
        >
          Reload Weather
        </Button>
        <Grid container spacing={2} direction={upSm ? 'row' : 'column'}>

          {/* conditions */}
          <FormGridItem xs={12} sm={6} lg={6} xl={3}>
            <Autocomplete
              freeSolo
              selectOnFocus
              blurOnSelect
              value={(value && value.conditions) ? value.conditions : ''}
              options={options.conditions}
              getOptionLabel={option => typeof option === 'string' ? option : option.name}
              onChange={(e, newValue) => {
                onWeatherChange('conditions', newValue);
              }}
              onInputChange={(e, newValue) => {
                if (e) {
                  onWeatherChange('conditions', { id: null, name: newValue });
                }
              }}
              renderInput={params =>
                <TextField
                  {...params}
                  label="General Conditions"
                  variant="outlined"
                  size="small"
                  onFocus={onFocus}
                  onBlur={onBlur}
                />}
            />
          </FormGridItem>

          {/* temperature */}
          <FormGridItem xs={12} sm={6} lg={6} xl={3}>
            <Autocomplete
              freeSolo
              selectOnFocus
              blurOnSelect
              value={(value && value.temperature) ? value.temperature : ''}
              options={options.temperature}
              getOptionLabel={option => typeof option === 'string' ? option : option.name}
              onChange={(e, newValue) => {
                onWeatherChange('temperature', newValue || { id: null, name: '' });
              }}
              onInputChange={(e, newValue) => {
                if (e) {
                  onWeatherChange('temperature', { id: null, name: newValue });
                }
              }}
              renderInput={params =>
                <TextField
                  {...params}
                  label="Temperature"
                  variant="outlined"
                  size="small"
                  onFocus={onFocus}
                  onBlur={onBlur}
                />}
            />
          </FormGridItem>

          {/* wind speed */}
          <FormGridItem xs={12} sm={6} lg={6} xl={3}>
            <Autocomplete
              freeSolo
              selectOnFocus
              value={(value && value.windSpeed) ? value.windSpeed : ''}
              options={options.windSpeed}
              getOptionLabel={option => typeof option === 'string' ? option : option.name}
              onChange={(e, newValue) => {
                onWeatherChange('windSpeed', newValue || { id: null, name: '' });
              }}
              onInputChange={(e, newValue) => {
                if (e) {
                  onWeatherChange('windSpeed', { id: null, name: newValue });
                }
              }}
              renderInput={params =>
                <TextField
                  {...params}
                  label="Wind Speed"
                  variant="outlined"
                  size="small"
                  onFocus={onFocus}
                  onBlur={onBlur}
                />}
            />
          </FormGridItem>

          {/* wind direction */}
          <FormGridItem xs={12} sm={6} lg={6} xl={3}>
            <Autocomplete
              freeSolo
              selectOnFocus
              blurOnSelect
              value={(value && value.windDirection) ? value.windDirection : ''}
              options={options.windDirection}
              getOptionLabel={option => typeof option === 'string' ? option : option.name}
              onChange={(e, newValue) => {
                onWeatherChange('windDirection', newValue || { id: null, name: '' });
              }}
              onInputChange={(e, newValue) => {
                if (e) {
                  onWeatherChange('windDirection', { id: null, name: newValue });
                }
              }}
              renderInput={params =>
                <TextField
                  {...params}
                  label="Wind Direction"
                  variant="outlined"
                  size="small"
                  onFocus={onFocus}
                  onBlur={onBlur}
                />}
            />
          </FormGridItem>

        </Grid>
    </>
  );
}